/**
 * Shared browser device-metadata extraction for FCM registration.
 *
 * Extracted verbatim from the AIM-lineage implementations (ONE
 * `registration.ts` / SMS `registerPush.ts`, both behavior-matched to AIM's
 * `getDeviceMetadata` in `aegix-aim-react/src/firebase/firebase.js`). Coarse
 * browser + OS strings from the user agent — deliberately heuristic, these
 * feed the backend's `device_model` / `os_version` registration fields for
 * fleet observability, nothing routing-critical.
 */

export interface WebPushDeviceMetadata {
  browser: string;
  os: string;
  locale: string;
  timezone: string;
  screenResolution: string;
}

/** Extract coarse browser + OS strings from the user agent (AIM parity). */
export function getDeviceMetadata(): WebPushDeviceMetadata {
  const ua = navigator.userAgent;
  let browser = 'Unknown Browser';
  let os = 'Unknown OS';

  if (ua.includes('Edg')) {
    const match = ua.match(/Edg\/(\d+)/);
    browser = match ? `Edge ${match[1]}` : 'Edge';
  } else if (ua.includes('Chrome')) {
    const match = ua.match(/Chrome\/(\d+)/);
    browser = match ? `Chrome ${match[1]}` : 'Chrome';
  } else if (ua.includes('Firefox')) {
    const match = ua.match(/Firefox\/(\d+)/);
    browser = match ? `Firefox ${match[1]}` : 'Firefox';
  } else if (ua.includes('Safari')) {
    const match = ua.match(/Version\/(\d+)/);
    browser = match ? `Safari ${match[1]}` : 'Safari';
  }

  if (ua.includes('Mac OS X')) {
    const match = ua.match(/Mac OS X (\d+[._]\d+)/);
    os = match ? `macOS ${match[1].replace('_', '.')}` : 'macOS';
  } else if (ua.includes('Windows NT')) {
    // Map the NT version to its named release — emitting the raw
    // `Windows 6.1` instead of `Windows 7` is a regression.
    const match = ua.match(/Windows NT (\d+\.\d+)/);
    const version = match ? match[1] : '';
    if (version === '10.0') os = 'Windows 10/11';
    else if (version === '6.3') os = 'Windows 8.1';
    else if (version === '6.2') os = 'Windows 8';
    else if (version === '6.1') os = 'Windows 7';
    else os = version ? `Windows ${version}` : 'Windows';
  } else if (ua.includes('Android')) {
    const match = ua.match(/Android (\d+)/);
    os = match ? `Android ${match[1]}` : 'Android';
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    const match = ua.match(/OS (\d+[._]\d+)/);
    os = match ? `iOS ${match[1].replace('_', '.')}` : 'iOS';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  }

  return {
    browser,
    os,
    locale: navigator.language || 'en-US',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    screenResolution: `${window.screen.width}x${window.screen.height}`,
  };
}
